import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import PageShell from "../components/PageShell";
import ImagePlaceholder from "../components/ImagePlaceholder";
import { getCategoryBySlug, getProjectsByCategory } from "../data/projects";

const CategoryDetail = () => {
  const { categorySlug } = useParams();
  const category = getCategoryBySlug(categorySlug);
  if (!category) return <Navigate to="/portfolio" replace />;
  const items = getProjectsByCategory(categorySlug);

  return (
    <PageShell testId="page-category">
      <section className="max-w-7xl mx-auto px-6 md:px-12 pt-4 pb-10">
        <Link
          to="/portfolio"
          data-testid="category-back-link"
          className="inline-flex items-center gap-2 font-manrope text-xs tracking-[0.2em] uppercase text-slate-500 dark:text-slate-400 dark:text-slate-500 hover:text-slate-900 dark:text-slate-100 transition"
        >
          <ArrowLeft size={14} /> Back to Portfolio
        </Link>

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className="mt-8 grid grid-cols-1 md:grid-cols-12 gap-8 items-end"
        >
          <div className="md:col-span-8">
            <div className="font-manrope text-[10px] tracking-[0.3em] uppercase text-[#A8D2FA] mb-4">
              Folder · {items.length} {items.length === 1 ? "project" : "projects"}
            </div>
            <h1
              data-testid="category-title"
              className="font-fraunces text-5xl md:text-7xl font-light text-slate-900 dark:text-slate-100 leading-[1.02] text-balance"
            >
              {category.label}
            </h1>
          </div>
          <div className="md:col-span-4">
            <p className="font-manrope text-slate-600 dark:text-slate-300 leading-relaxed">
              {category.description}
            </p>
          </div>
        </motion.div>
      </section>

      <section
        className="max-w-7xl mx-auto px-6 md:px-12 pb-24"
        data-testid="category-projects"
      >
        {items.length === 0 ? (
          <div className="glass rounded-[24px] p-10 text-center">
            <p className="font-manrope text-slate-500 dark:text-slate-400 dark:text-slate-500">
              No projects in this folder yet — new work coming soon.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-16">
            {items.map((p, i) => (
              <ProjectCard key={p.slug} project={p} index={i} />
            ))}
          </div>
        )}
      </section>
    </PageShell>
  );
};

const ProjectCard = ({ project, index }) => {
  const offset = index % 2 === 0 ? "md:mt-0" : "md:mt-16";
  return (
    <Link
      to={`/portfolio/${project.slug}`}
      data-testid={`category-project-${project.slug}`}
      className={`group block ${offset}`}
    >
      <ImagePlaceholder
        src={project.hero}
        alt={`${project.title} — cover`}
        ratio="aspect-[4/5]"
        rounded="rounded-[28px]"
        label={project.category.toUpperCase()}
        imgClassName={project.heroObjectPosition || ""}
      />
      <div className="mt-6 flex items-start justify-between gap-4">
        <div>
          <div className="font-manrope text-[10px] tracking-[0.3em] uppercase text-slate-400 dark:text-slate-500">
            {project.category} · {project.date}
          </div>
          <h3 className="font-fraunces text-2xl md:text-3xl font-light text-slate-900 dark:text-slate-100 mt-2 group-hover:text-slate-700 dark:text-slate-200 transition">
            {project.title}
          </h3>
          <p className="mt-3 font-manrope text-sm text-slate-500 dark:text-slate-400 dark:text-slate-500 leading-relaxed max-w-md">
            {project.description}
          </p>
        </div>
        <div className="glass rounded-full p-2.5 mt-1 transition-transform group-hover:-translate-y-1 group-hover:translate-x-1">
          <ArrowUpRight size={16} className="text-slate-700 dark:text-slate-200" />
        </div>
      </div>
    </Link>
  );
};

export default CategoryDetail;
